import type {JSX} from 'react'
import React, {
	useState,
	useRef,
	useCallback,
	useEffect,
	forwardRef,
} from 'react'
import {clsx} from 'clsx'
import Image from 'next/image'
import Slider from 'react-slick'
import type {Settings as SlickSettings} from 'react-slick'
import {createPortal} from 'react-dom'
import {loadImageFromKontentAI} from '@/image-loader'
import styles from './media-slider.module.css'
import {
	getBrandColorValue,
	getThumbnailContainerClasses,
	getSafeBrandBorderClass,
	getSliderContainerClasses,
	getSliderLayoutClasses,
	getSliderMainImageClasses,
	scrollThumbnailToActiveIndex,
	calculateThumbnailContainerHeight,
	getMediaType,
} from './utils'

// Types for the UI component
export interface MediaItem {
	id: string
	url: string
	type: 'image' | 'video'
	videoType?: 'youtube' | 'wistia'
	alt?: string
	thumbnail?: string
	title?: string
}

export interface MediaSliderConfig {
	arrowColor?: string
	enableZoom?: boolean
	autoplay?: boolean
	autoplaySpeed?: number
	enableLazy?: boolean
	showThumbnails?: boolean
	thumbnailPosition?: 'bottom' | 'left' | 'right'
	position?: 'left' | 'center' | 'right' | 'full'
}

interface ArrowProps {
	onClick?: () => void
	arrowColor: string
	onKeyboardActivate?: () => void
}

const CustomPrevArrow = ({
	onClick,
	arrowColor,
	onKeyboardActivate,
}: ArrowProps): JSX.Element => {
	const colorValue = getBrandColorValue(arrowColor)

	const handleClick = (): void => {
		onClick?.()
		onKeyboardActivate?.()
	}

	const handleKeyDown = (e: React.KeyboardEvent): void => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			e.stopPropagation() // Prevent event from bubbling up to main slider
			handleClick()
		}
	}

	return (
		<button
			aria-label="Previous image"
			className={`absolute !left-[-35px] top-1/2 z-10 !mt-0 flex !h-8 !w-8 -translate-y-1/2 items-center justify-center rounded-full shadow-lg after:!content-none md:!left-[-4rem] md:!h-9 md:!w-9 bg-${arrowColor} hover:bg-${arrowColor}/90`}
			onClick={() => {
				handleClick()
			}}
			onKeyDown={handleKeyDown}
			type="button"
		>
			<svg
				className="h-6 w-6 md:h-8 md:w-8"
				fill="none"
				stroke={colorValue === '#ffffff' ? 'black' : 'white'}
				strokeWidth={2}
				viewBox="0 0 24 24"
			>
				<path
					d="M19 12H5m0 0l7 7m-7-7l7-7"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</button>
	)
}

const CustomNextArrow = ({
	onClick,
	arrowColor,
	onKeyboardActivate,
}: ArrowProps): JSX.Element => {
	const colorValue = getBrandColorValue(arrowColor)

	const handleClick = (): void => {
		onClick?.()
		onKeyboardActivate?.()
	}

	const handleKeyDown = (e: React.KeyboardEvent): void => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			e.stopPropagation() // Prevent event from bubbling up to main slider
			handleClick()
		}
	}

	return (
		<button
			aria-label="Next image"
			className={`absolute !right-[-35px] top-1/2 z-10 !mt-0 flex !h-8 !w-8 -translate-y-1/2 items-center justify-center rounded-full shadow-lg after:!content-none md:!right-[-4rem] md:!h-9 md:!w-9 bg-${arrowColor} hover:bg-${arrowColor}/90`}
			onClick={() => {
				handleClick()
			}}
			onKeyDown={handleKeyDown}
			type="button"
		>
			<svg
				className="h-6 w-6 md:h-8 md:w-8"
				fill="none"
				stroke={colorValue === '#ffffff' ? 'black' : 'white'}
				strokeWidth={2}
				viewBox="0 0 24 24"
			>
				<path
					d="M5 12h14m0 0l-7-7m7 7l-7 7"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</button>
	)
}

interface MediaSliderProps {
	mediaItems: MediaItem[]
	config?: MediaSliderConfig
	className?: string
	containerClassName?: string
	mainImageClassName?: string
	thumbnailClassName?: string
	fullWidth?: boolean
	enlargeText?: string
	productTitleText?: string
	enlargeImageText?: string
	enlargeVideoText?: string
	onMediaChange?: (mediaItem: MediaItem, index: number) => void
	renderVideoComponent?: (
		item: MediaItem,
		forceInteractive?: boolean
	) => JSX.Element
}

export const MediaSlider = forwardRef<Slider, MediaSliderProps>(
	(
		{
			mediaItems,
			config = {},
			className,
			containerClassName,
			mainImageClassName,
			thumbnailClassName,
			fullWidth = false,
			productTitleText,
			enlargeImageText,
			enlargeVideoText,
			onMediaChange,
			renderVideoComponent,
		},
		_ref
	) => {
		const {
			enableZoom = true,
			autoplay = false,
			autoplaySpeed = 3000,
			enableLazy = true,
			showThumbnails = true,
			thumbnailPosition = 'left',
			position = 'right',
			arrowColor = 'elanco-blue',
		} = config

		const [currentSlide, setCurrentSlide] = useState(0)
		const [announcement, setAnnouncement] = useState('')
		const [isZoomed, setIsZoomed] = useState(false)
		const [zoomedMedia, setZoomedMedia] = useState<MediaItem | null>(null)
		const [isMobile, setIsMobile] = useState(false)
		const mainSliderRef = useRef<Slider>(null)
		const thumbnailSliderRef = useRef<HTMLDivElement>(null)
		const modalThumbnailRef = useRef<HTMLDivElement>(null)
		const mainContainerRef = useRef<HTMLDivElement>(null)
		const modalRef = useRef<HTMLDivElement>(null)
		const modalCloseButtonRef = useRef<HTMLButtonElement>(null)
		const enlargeButtonRef = useRef<HTMLButtonElement>(null)

		const safeBorderClass = getSafeBrandBorderClass(arrowColor)
		const colorValue = getBrandColorValue(arrowColor)

		// Generate dot colors based on the arrow color (since getBrandColorValue returns hex colors)
		const dotColors = {
			'--dots-active-color': colorValue,
			'--dots-inactive-color': `${colorValue}66`, // 40% opacity in hex
			'--dots-hover-color': `${colorValue}CC`, // 80% opacity in hex
		} as React.CSSProperties

		// Auto-scroll thumbnail to keep active item visible
		const scrollThumbToActiveIndex = useCallback(
			(activeIndex: number) => {
				if (thumbnailSliderRef.current) {
					scrollThumbnailToActiveIndex(
						thumbnailSliderRef.current,
						activeIndex,
						mediaItems.length,
						fullWidth
					)
				}
			},
			[mediaItems.length, fullWidth]
		)

		// Auto-scroll horizontal thumbnails in modal
		const scrollModalThumbToActiveIndex = useCallback(
			(activeIndex: number) => {
				if (modalThumbnailRef.current) {
					scrollThumbnailToActiveIndex(
						modalThumbnailRef.current,
						activeIndex,
						mediaItems.length,
						fullWidth,
						true // isHorizontal = true
					)
				}
			},
			[mediaItems.length, fullWidth]
		)

		// Calculate dynamic height for thumbnail container
		const calculateThumbnailHeight = useCallback(() => {
			if (mainContainerRef.current) {
				return calculateThumbnailContainerHeight(
					mainContainerRef.current
				)
			}
			return 'calc(100vh - 200px)'
		}, [])

		// Update thumbnail height when component mounts or main container changes
		useEffect(() => {
			const updateHeight = (): void => {
				// Only update height if we're in desktop mode and elements exist
				if (
					!isMobile &&
					thumbnailSliderRef.current &&
					mainContainerRef.current
				) {
					const dynamicHeight = calculateThumbnailHeight()
					thumbnailSliderRef.current.style.height = dynamicHeight
				}
			}

			// Use setTimeout to ensure DOM is fully rendered
			const timeoutId = setTimeout(updateHeight, 100)

			const handleResize = (): void => {
				updateHeight()
			}
			window.addEventListener('resize', handleResize)

			return () => {
				clearTimeout(timeoutId)
				window.removeEventListener('resize', handleResize)
			}
		}, [calculateThumbnailHeight, mediaItems.length, fullWidth, isMobile])

		// Check if device is mobile
		useEffect(() => {
			const checkMobile = (): void => {
				const wasMobile = isMobile
				const nowMobile = window.innerWidth < 768 // Use 768px to treat tablets as desktop
				setIsMobile(nowMobile)

				// If switching from mobile to desktop, recalculate thumbnail height
				if (
					wasMobile &&
					!nowMobile &&
					thumbnailSliderRef.current &&
					mainContainerRef.current
				) {
					// Use a small delay to ensure layout has updated
					setTimeout(() => {
						if (
							thumbnailSliderRef.current &&
							mainContainerRef.current
						) {
							const dynamicHeight = calculateThumbnailHeight()
							thumbnailSliderRef.current.style.height =
								dynamicHeight
						}
					}, 100)
				}

				// If switching from desktop to mobile, reset thumbnail height
				if (!wasMobile && nowMobile && thumbnailSliderRef.current) {
					thumbnailSliderRef.current.style.height = 'auto'
				}
			}

			// Check on mount
			checkMobile()

			// Add resize listener
			window.addEventListener('resize', checkMobile)

			return () => {
				window.removeEventListener('resize', checkMobile)
			}
		}, [isMobile, calculateThumbnailHeight])
		// Handle thumbnail click
		const handleThumbnailClick = useCallback(
			(index: number) => {
				setCurrentSlide(index)
				if (mainSliderRef.current) {
					mainSliderRef.current.slickGoTo(index)
				}
				scrollThumbToActiveIndex(index)
			},
			[scrollThumbToActiveIndex]
		)

		// Handle slide change
		const handleSlideChange = useCallback(
			(currentSlideIndex: number) => {
				setCurrentSlide(currentSlideIndex)
				scrollThumbToActiveIndex(currentSlideIndex)
				const mediaItem = mediaItems[currentSlideIndex]
				if (onMediaChange && mediaItem) {
					onMediaChange(mediaItem, currentSlideIndex)
				}
			},
			[onMediaChange, mediaItems, scrollThumbToActiveIndex]
		)

		// Handle keyboard navigation for arrows
		const handlePrevKeyboard = useCallback(() => {
			const newIndex =
				currentSlide > 0 ? currentSlide - 1 : mediaItems.length - 1
			setCurrentSlide(newIndex)
			const mediaType = getMediaType(mediaItems[newIndex])
			setAnnouncement(
				`Previous ${mediaType}. Now showing ${mediaType} ${
					newIndex + 1
				} of ${mediaItems.length}`
			)
			if (mainSliderRef.current) {
				mainSliderRef.current.slickGoTo(newIndex)
			}
			handleSlideChange(newIndex)
		}, [currentSlide, mediaItems, handleSlideChange])

		const handleNextKeyboard = useCallback(() => {
			const newIndex =
				currentSlide < mediaItems.length - 1 ? currentSlide + 1 : 0
			setCurrentSlide(newIndex)
			const mediaType = getMediaType(mediaItems[newIndex])
			setAnnouncement(
				`Next ${mediaType}. Now showing ${mediaType} ${
					newIndex + 1
				} of ${mediaItems.length}`
			)
			if (mainSliderRef.current) {
				mainSliderRef.current.slickGoTo(newIndex)
			}
			handleSlideChange(newIndex)
		}, [currentSlide, mediaItems, handleSlideChange])

		const handleEnlargeClick = useCallback(() => {
			const currentMedia = mediaItems[currentSlide]
			if (enableZoom && currentMedia) {
				setZoomedMedia(currentMedia)
				setIsZoomed(true)
			}
		}, [enableZoom, mediaItems, currentSlide])

		const handleCloseZoom = useCallback(() => {
			setIsZoomed(false)
			setZoomedMedia(null)
			// Return focus to the enlarge button when modal closes
			setTimeout(() => {
				if (enlargeButtonRef.current) {
					enlargeButtonRef.current.focus()
				}
			}, 100)
		}, []) // Focus management for modal
		useEffect(() => {
			if (isZoomed && modalCloseButtonRef.current) {
				// Focus the close button when modal opens
				modalCloseButtonRef.current.focus()
			}
		}, [isZoomed])

		// Handle escape key and focus trapping for modal
		useEffect(() => {
			const handleKeyDown = (e: KeyboardEvent): void => {
				if (e.key === 'Escape' && isZoomed) {
					handleCloseZoom()
					return
				}

				// Focus trap within modal
				if (isZoomed && e.key === 'Tab' && modalRef.current) {
					e.preventDefault()

					const focusableElements = modalRef.current.querySelectorAll(
						'button, [tabindex="0"], [role="button"][tabindex="0"]'
					)
					const focusableArray = Array.from(focusableElements).filter(
						(element) => {
							const htmlElement = element as HTMLElement
							const isDisabled = (
								htmlElement as HTMLButtonElement
							).disabled
							return (
								!isDisabled &&
								htmlElement.offsetParent !== null &&
								getComputedStyle(htmlElement).visibility !==
									'hidden'
							)
						}
					) as HTMLElement[]

					if (focusableArray.length === 0) return

					const currentIndex = focusableArray.findIndex(
						(element) => element === document.activeElement
					)

					let nextIndex: number
					if (e.shiftKey) {
						// Shift + Tab - go to previous element
						nextIndex =
							currentIndex <= 0
								? focusableArray.length - 1
								: currentIndex - 1
					} else {
						// Tab - go to next element
						nextIndex =
							currentIndex >= focusableArray.length - 1
								? 0
								: currentIndex + 1
					}

					focusableArray[nextIndex]?.focus()
				}
			}

			if (isZoomed) {
				document.addEventListener('keydown', handleKeyDown)
				return () => {
					document.removeEventListener('keydown', handleKeyDown)
				}
			}
		}, [isZoomed, handleCloseZoom])

		// Prevent focus from entering slider content
		useEffect(() => {
			const sliderContainer = mainContainerRef.current

			if (sliderContainer) {
				// Find all focusable elements within the slider
				const focusableElements = sliderContainer.querySelectorAll(
					'.slick-track button, .slick-track [href], .slick-track input, .slick-track select, .slick-track textarea, .slick-track [tabindex]:not([tabindex="-1"]), .slick-track iframe, .slick-track video, .slick-track audio'
				)

				// Make them all non-focusable
				focusableElements.forEach((element) => {
					const htmlElement = element as HTMLElement
					htmlElement.setAttribute('tabindex', '-1')
				})
			}
		}, [mediaItems, currentSlide])

		// Auto-scroll thumbnails when currentSlide changes
		useEffect(() => {
			scrollThumbToActiveIndex(currentSlide)
			// Also scroll modal thumbnails if modal is open
			if (isZoomed) {
				scrollModalThumbToActiveIndex(currentSlide)
			}
		}, [
			currentSlide,
			scrollThumbToActiveIndex,
			scrollModalThumbToActiveIndex,
			isZoomed,
		])

		const renderThumbnailForVideo = useCallback(
			(item: MediaItem): JSX.Element => {
				const thumbnailContent = item.thumbnail ? (
					<Image
						alt={item.alt || item.title || 'Video thumbnail'}
						className="h-full w-full object-cover"
						height={80}
						loader={loadImageFromKontentAI}
						src={item.thumbnail}
						width={80}
					/>
				) : (
					<div className="flex h-full w-full items-center justify-center bg-gray-200">
						<div className="text-xs text-gray-500">Video</div>
					</div>
				)

				return (
					<div className="relative h-full w-full">
						{thumbnailContent}
						<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
							<div className="flex h-6 w-6 items-center justify-center rounded-full bg-black bg-opacity-60">
								<svg
									className="h-3 w-3 text-white"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M8 5v14l11-7z" />
								</svg>
							</div>
						</div>
					</div>
				)
			},
			[]
		)

		const renderMediaItem = useCallback(
			(
				item: MediaItem,
				isMain = false,
				forceInteractive = false,
				useLazy = false,
				slideIndex?: number
			): JSX.Element => {
				if (item.type === 'video') {
					if (isMain && renderVideoComponent) {
						return renderVideoComponent(item, forceInteractive)
					}
					return renderThumbnailForVideo(item)
				}

				const shouldLoad =
					!useLazy ||
					slideIndex === undefined ||
					slideIndex === currentSlide ||
					Math.abs(slideIndex - currentSlide) <= 1 ||
					isMain

				return (
					<div
						className={clsx(
							'relative flex items-center justify-center ',
							isMain ? 'aspect-[4/3]' : 'h-full w-full'
						)}
					>
						{useLazy && isMain && !shouldLoad ? (
							<div className="flex h-full w-full items-center justify-center bg-gray-100">
								<div className="border-gray-900 h-8 w-8 animate-spin rounded-full border-b-2" />
							</div>
						) : (
							<Image
								alt={item.alt || item.title || 'Product image'}
								className="h-full w-full object-contain"
								height={isMain ? 400 : 80}
								loader={loadImageFromKontentAI}
								src={item.url}
								width={isMain ? 400 : 80}
							/>
						)}
					</div>
				)
			},
			[renderVideoComponent, renderThumbnailForVideo, currentSlide]
		)

		// Main slider settings
		const mainSettings: SlickSettings = {
			dots: mediaItems.length > 1,
			dotsClass: 'slick-dots',
			infinite: mediaItems.length > 1,
			speed: 500,
			slidesToShow: 1,
			slidesToScroll: 1,
			autoplay,
			autoplaySpeed,
			arrows: mediaItems.length > 1,
			prevArrow: (
				<CustomPrevArrow
					arrowColor={arrowColor}
					onKeyboardActivate={handlePrevKeyboard}
				/>
			),
			nextArrow: (
				<CustomNextArrow
					arrowColor={arrowColor}
					onKeyboardActivate={handleNextKeyboard}
				/>
			),
			beforeChange: (current: number, next: number) => {
				handleSlideChange(next)
			},
			afterChange: (current: number) => {
				setCurrentSlide(current)
			},
		}

		if (mediaItems.length === 0) {
			return (
				<div className="flex h-96 items-center justify-center rounded-lg bg-gray-100">
					<p className="text-gray-500">No media available</p>
				</div>
			)
		}

		return (
			<>
				{/* ARIA live region for screen reader announcements */}
				<div aria-atomic="true" aria-live="polite" className="sr-only">
					{announcement}
				</div>
				<div
					aria-label={`Product image carousel with ${
						mediaItems.length
					} ${mediaItems.length === 1 ? 'image' : 'images'}`}
					className={getSliderContainerClasses(
						position,
						fullWidth,
						className
					)}
					role="region"
				>
					<div className={getSliderLayoutClasses(containerClassName)}>
						{/* Thumbnail Slider */}
						{showThumbnails && mediaItems.length > 1 ? (
							<div
								className={clsx(
									getThumbnailContainerClasses(
										thumbnailPosition,
										fullWidth
									),
									thumbnailClassName,
									'hidden md:flex' // Hide thumbnails on mobile, show on tablet and desktop
								)}
							>
								{/* Desktop only: Vertical scroll */}
								<div
									className="flex flex-col overflow-y-auto scrollbar-none"
									ref={thumbnailSliderRef}
								>
									{mediaItems.map((item, index) => {
										const desktopThumbnailSize = fullWidth
											? '100px'
											: '70px'

										return (
											<div
												aria-current={
													currentSlide === index
														? 'true'
														: undefined
												}
												aria-label={`${
													getMediaType(item) ===
													'video'
														? 'Video'
														: 'Image'
												} ${index + 1} of ${
													mediaItems.length
												}${
													item.alt
														? `: ${item.alt}`
														: ''
												}`}
												className={clsx(
													'thumbnail-item mb-2 flex-shrink-0 cursor-pointer overflow-hidden border-2 transition-all duration-200 hover:shadow-sm',
													currentSlide === index
														? `border-2 ${safeBorderClass}`
														: 'border-gray-200 hover:border-gray-300'
												)}
												key={`desktop-thumb-${item.id}`}
												onClick={() => {
													handleThumbnailClick(index)
												}}
												onKeyDown={(e) => {
													if (
														e.key === 'Enter' ||
														e.key === ' '
													) {
														e.preventDefault()
														handleThumbnailClick(
															index
														)
													}
												}}
												role="button"
												style={{
													height: desktopThumbnailSize,
												}}
												tabIndex={0}
											>
												{renderMediaItem(item)}
											</div>
										)
									})}
								</div>
							</div>
						) : null}

						{/* Main Image Slider */}
						<div
							className={clsx(
								getSliderMainImageClasses(
									thumbnailPosition,
									mainImageClassName
								),
								styles.sliderContainer
							)}
							ref={mainContainerRef}
							style={dotColors}
						>
							<div
								aria-label={`${
									getMediaType(mediaItems[currentSlide]) ===
									'video'
										? 'Video'
										: 'Image'
								} ${currentSlide + 1} of ${mediaItems.length}`}
								className="slick-navigation-container relative aspect-[4/3]"
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault()
										if (enableZoom) {
											handleEnlargeClick()
										}
									}
								}}
								role="button"
								tabIndex={0}
							>
								<div>
									<Slider
										{...mainSettings}
										ref={mainSliderRef}
									>
										{mediaItems.map((item, index) => (
											<div key={item.id}>
												<div
													className={clsx(
														'transition-opacity duration-200',
														enableZoom && isMobile
															? 'cursor-pointer hover:opacity-90'
															: 'cursor-default'
													)}
													onClick={() => {
														// Enable zoom on mobile click
														if (
															enableZoom &&
															isMobile
														) {
															handleEnlargeClick()
														}
													}}
													onKeyDown={(e) => {
														if (
															enableZoom &&
															!isMobile &&
															(e.key ===
																'Enter' ||
																e.key === ' ')
														) {
															e.preventDefault()
															handleEnlargeClick()
														}
													}}
													role={
														enableZoom && !isMobile
															? 'button'
															: undefined
													}
													tabIndex={
														enableZoom && !isMobile
															? 0
															: -1
													}
												>
													{renderMediaItem(
														item,
														true,
														false,
														enableLazy,
														index
													)}
												</div>
											</div>
										))}
									</Slider>
								</div>
							</div>
							{enableZoom && mediaItems[currentSlide] ? (
								<div className="mt-2 hidden justify-center md:flex lg:-mt-7 xl:-mt-2">
									<button
										className="text-blue-600 flex items-center gap-1 text-sm underline hover:text-blue-800"
										onClick={handleEnlargeClick}
										ref={enlargeButtonRef}
										type="button"
									>
										<svg
											className="h-4 w-4"
											fill="none"
											stroke="currentColor"
											strokeWidth={2}
											viewBox="0 0 24 24"
										>
											<path
												d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
										{mediaItems[currentSlide]?.type ===
										'image'
											? enlargeImageText
											: enlargeVideoText}
									</button>
								</div>
							) : null}
						</div>
					</div>
				</div>

				{/* Zoom Modal */}
				{isZoomed && zoomedMedia
					? createPortal(
							<div
								aria-label="Enlarged media view"
								aria-modal="true"
								className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75"
								role="dialog"
								tabIndex={-1}
							>
								<div
									className="relative flex h-[85vh] w-[90vw] max-w-6xl flex-col overflow-hidden bg-white shadow-2xl lg:flex-row"
									ref={modalRef}
								>
									{/* Main zoomed content */}
									<div className="relative flex flex-1 items-center justify-center bg-white p-4 pt-16">
										{zoomedMedia.type === 'video' &&
										renderVideoComponent ? (
											renderVideoComponent(
												zoomedMedia,
												true
											)
										) : (
											<Image
												alt={
													zoomedMedia.alt ||
													zoomedMedia.title ||
													'Enlarged image'
												}
												className="max-h-full max-w-full object-contain"
												height={600}
												loader={loadImageFromKontentAI}
												src={zoomedMedia.url}
												width={800}
											/>
										)}
									</div>

									{/* Thumbnail grid in modal */}
									{mediaItems.length > 1 ? (
										<div className="bg-gray-50 p-4">
											{/* Product Title */}
											{productTitleText ? (
												<div className="mb-2 text-left lg:mb-2">
													<h3 className="text-gray-900 text-lg font-semibold">
														{productTitleText}
													</h3>
												</div>
											) : null}

											{/* Mobile: Horizontal scrolling thumbnails */}
											<div
												className="flex gap-2 overflow-x-auto scrollbar-none lg:hidden"
												ref={modalThumbnailRef}
											>
												{mediaItems.map(
													(item, index) => (
														<div
															aria-label={`${
																getMediaType(
																	item
																) === 'video'
																	? 'Video'
																	: 'Image'
															} ${index + 1} of ${
																mediaItems.length
															}${
																item.alt
																	? `: ${item.alt}`
																	: ''
															}`}
															className={clsx(
																'thumbnail-item h-16 w-16 flex-shrink-0 cursor-pointer overflow-hidden border-2 transition-all duration-200',
																currentSlide ===
																	index
																	? `border-2 ${safeBorderClass}`
																	: 'border-gray-200 hover:border-gray-300'
															)}
															key={`modal-mobile-thumb-${item.id}`}
															onClick={() => {
																setCurrentSlide(
																	index
																)
																setZoomedMedia(
																	item
																)
																scrollModalThumbToActiveIndex(
																	index
																)
																if (
																	mainSliderRef.current
																) {
																	mainSliderRef.current.slickGoTo(
																		index
																	)
																}
															}}
															onKeyDown={(e) => {
																if (
																	e.key ===
																		'Enter' ||
																	e.key ===
																		' '
																) {
																	e.preventDefault()
																	setCurrentSlide(
																		index
																	)
																	setZoomedMedia(
																		item
																	)
																	scrollModalThumbToActiveIndex(
																		index
																	)
																	if (
																		mainSliderRef.current
																	) {
																		mainSliderRef.current.slickGoTo(
																			index
																		)
																	}
																}
															}}
															role="button"
															tabIndex={0}
														>
															{renderMediaItem(
																item,
																false,
																true
															)}
														</div>
													)
												)}
											</div>

											{/* Desktop: Vertical scrolling grid */}
											<div
												className="hidden max-h-[calc(85vh-6rem)] w-[18vw] overflow-y-auto pt-2 scrollbar-none lg:block"
												style={{
													minWidth: '120px',
													maxWidth: '200px',
												}}
											>
												<div className="grid grid-cols-2 gap-2">
													{mediaItems.map(
														(item, index) => (
															<div
																aria-label={`${
																	getMediaType(
																		item
																	) ===
																	'video'
																		? 'Video'
																		: 'Image'
																} ${
																	index + 1
																} of ${
																	mediaItems.length
																}${
																	item.alt
																		? `: ${item.alt}`
																		: ''
																}`}
																className={clsx(
																	'aspect-square cursor-pointer overflow-hidden border-2 transition-all duration-200 hover:shadow-md',
																	currentSlide ===
																		index
																		? `border-2 ${safeBorderClass}`
																		: 'border-gray-200 hover:border-gray-300'
																)}
																key={`modal-desktop-thumb-${item.id}`}
																onClick={() => {
																	setCurrentSlide(
																		index
																	)
																	setZoomedMedia(
																		item
																	)
																	if (
																		mainSliderRef.current
																	) {
																		mainSliderRef.current.slickGoTo(
																			index
																		)
																	}
																}}
																onKeyDown={(
																	e
																) => {
																	if (
																		e.key ===
																			'Enter' ||
																		e.key ===
																			' '
																	) {
																		e.preventDefault()
																		setCurrentSlide(
																			index
																		)
																		setZoomedMedia(
																			item
																		)
																		if (
																			mainSliderRef.current
																		) {
																			mainSliderRef.current.slickGoTo(
																				index
																			)
																		}
																	}
																}}
																role="button"
																tabIndex={0}
															>
																<div className="h-full w-full">
																	{renderMediaItem(
																		item,
																		false,
																		true
																	)}
																</div>
															</div>
														)
													)}
												</div>
											</div>
										</div>
									) : null}

									<button
										aria-label="Close enlarged view"
										className="text-gray-900 absolute left-4 top-4 z-10 flex h-10 w-10 items-center justify-center"
										onClick={handleCloseZoom}
										ref={modalCloseButtonRef}
										type="button"
									>
										<svg
											className="h-6 w-6"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												d="M6 18L18 6M6 6l12 12"
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
											/>
										</svg>
									</button>
								</div>
							</div>,
							document.body
						)
					: null}
			</>
		)
	}
)

MediaSlider.displayName = 'MediaSlider'
